import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { TokenInfo, ClauseInfo, NounChunkInfo, SentenceInfo } from '../../types';
import { isProcessToken, isParticipantToken } from '../../utils/groupingRules';
import { useSoundHandlers } from '../../hooks/useSoundEffects';

interface Step4Props {
  tokens: TokenInfo[];
  clauses: ClauseInfo[];
  nounChunks: NounChunkInfo[];
  sentences: SentenceInfo[];
  onComplete: (score: number) => void;
  isCompleted: boolean;
  showProcessHint?: boolean;
  showParticipantHint?: boolean;
}

// 小句片段结构
interface ClauseSegment {
  id: string;
  text: string;
  tokens: TokenInfo[];
  correctLevel: number;
}

// 将 tokens 按 cls_level 变化分段
function segmentByClauses(tokens: TokenInfo[]): ClauseSegment[] {
  const segments: ClauseSegment[] = [];
  let currentSegment: TokenInfo[] = [];
  let currentLevel: number | null = null;
  let segmentId = 0;
  
  tokens.forEach((token) => {
    // 跳过纯空格
    if (token.pos === 'SPACE') return;
    
    // 标点符号始终加入当前片段（不单独成段）
    if (token.pos === 'PUNCT') {
      if (currentSegment.length > 0) {
        currentSegment.push(token);
      }
      return;
    }
    
    if (currentLevel === null) {
      currentLevel = token.cls_level;
      currentSegment.push(token);
    } else if (token.cls_level === currentLevel) {
      currentSegment.push(token);
    } else {
      // 层级变化，保存当前段
      if (currentSegment.length > 0) {
        segments.push({
          id: `segment-${segmentId++}`,
          text: currentSegment.map(t => t.text).join(' ').replace(/ ([,.])/g, '$1'),
          tokens: currentSegment,
          correctLevel: currentLevel,
        });
      }
      currentSegment = [token];
      currentLevel = token.cls_level;
    }
  });
  
  // 处理最后一段
  if (currentSegment.length > 0 && currentLevel !== null) {
    segments.push({
      id: `segment-${segmentId}`,
      text: currentSegment.map(t => t.text).join(' ').replace(/ ([,.])/g, '$1'),
      tokens: currentSegment,
      correctLevel: currentLevel,
    });
  }
  
  return segments;
}

export default function Step4Clause({ 
  tokens, 
  onComplete, 
  showProcessHint = false,
  showParticipantHint = false,
}: Step4Props) {
  const { playClick } = useSoundHandlers();
  
  // 分段
  const segments = useMemo(() => segmentByClauses(tokens), [tokens]);
  
  // 学生设置的层级（初始都是 0）
  const [studentLevels, setStudentLevels] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    segments.forEach(seg => {
      initial[seg.id] = 0;
    });
    return initial;
  });
  
  const [validated, setValidated] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    correct: string[];
    wrong: string[];
  } | null>(null);

  // 获取最大正确层级
  const maxLevel = useMemo(() => 
    Math.max(...segments.map(s => s.correctLevel), 0), 
    [segments]
  );

  const handleLevelChange = (segmentId: string, delta: number) => {
    if (validated) return;
    playClick(); // 播放点击音效
    setStudentLevels(prev => ({
      ...prev,
      [segmentId]: Math.max(0, Math.min(maxLevel + 1, (prev[segmentId] || 0) + delta)),
    }));
  };

  const handleValidate = () => {
    const correct: string[] = [];
    const wrong: string[] = [];
    
    segments.forEach(seg => {
      if (studentLevels[seg.id] === seg.correctLevel) {
        correct.push(seg.id);
      } else {
        wrong.push(seg.id);
      }
    });
    
    const score = segments.length > 0 ? correct.length / segments.length : 0;
    setValidationResult({ correct, wrong });
    setValidated(true);
    onComplete(score);
  };

  const handleReset = () => {
    const initial: Record<string, number> = {};
    segments.forEach(seg => {
      initial[seg.id] = 0;
    });
    setStudentLevels(initial);
    setValidated(false);
    setValidationResult(null);
  };

  const getSegmentStatus = (segmentId: string): 'correct' | 'wrong' | null => {
    if (!validationResult) return null;
    if (validationResult.correct.includes(segmentId)) return 'correct';
    if (validationResult.wrong.includes(segmentId)) return 'wrong';
    return null;
  };

  // 层级颜色
  const getLevelColor = (level: number) => {
    const colors = ['cyan', 'purple', 'yellow', 'pink', 'orange', 'green'];
    return colors[level % colors.length];
  };

  return (
    <div>
      {/* 小句片段列表 */}
      <div className="p-6 rounded-lg bg-cyber-darker/50 border border-yellow-500/10 mb-6 space-y-2">
        {segments.map((segment) => {
          const level = studentLevels[segment.id] || 0;
          const status = getSegmentStatus(segment.id);
          const color = getLevelColor(level);
          
          return (
            <motion.div
              key={segment.id}
              layout
              className={`
                flex items-center gap-3 p-3 rounded-lg transition-all duration-200
                ${status === 'correct' ? 'bg-green-500/20 border border-green-500/50' : ''}
                ${status === 'wrong' ? 'bg-red-500/20 border border-red-500/50' : ''}
                ${!status ? 'bg-gray-800/50 border border-gray-700/50' : ''}
              `}
              style={{ marginLeft: `${level * 32}px` }}
            >
              {/* 层级调整按钮 */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleLevelChange(segment.id, -1)}
                  disabled={validated || level === 0}
                  className="w-7 h-7 flex items-center justify-center rounded bg-gray-700/50 hover:bg-gray-600/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-300" />
                </button>
                <span className={`
                  w-8 text-center font-mono text-sm font-bold
                  ${color === 'cyan' ? 'text-cyan-400' : ''}
                  ${color === 'purple' ? 'text-purple-400' : ''}
                  ${color === 'yellow' ? 'text-yellow-400' : ''}
                  ${color === 'pink' ? 'text-pink-400' : ''}
                  ${color === 'orange' ? 'text-orange-400' : ''}
                  ${color === 'green' ? 'text-green-400' : ''}
                `}>
                  {level}
                </span>
                <button
                  onClick={() => handleLevelChange(segment.id, 1)}
                  disabled={validated}
                  className="w-7 h-7 flex items-center justify-center rounded bg-gray-700/50 hover:bg-gray-600/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </button>
              </div>

              {/* 小句文本 */}
              <div className="flex-1 min-w-0">
                <p className={`
                  font-mono text-base leading-relaxed
                  ${status === 'correct' ? 'text-green-200' : ''}
                  ${status === 'wrong' ? 'text-red-200' : ''}
                  ${!status ? 'text-gray-200' : ''}
                `}>
                  {segment.tokens.map((token, idx) => {
                    const isProcess = showProcessHint && isProcessToken(token, tokens);
                    const isParticipant = showParticipantHint && isParticipantToken(token, tokens);
                    
                    return (
                      <span
                        key={token.id}
                        className={`
                          ${isProcess ? 'text-red-300 font-semibold' : ''}
                          ${isParticipant ? 'text-blue-300 font-semibold' : ''}
                        `}
                      >
                        {token.text}
                        {idx < segment.tokens.length - 1 && token.pos !== 'PUNCT' && ' '}
                      </span>
                    );
                  })}
                </p>
              </div>

              {/* 验证后显示正确层级 */}
              {validated && status === 'wrong' && (
                <span className="text-xs text-yellow-400 flex-shrink-0">
                  正确: L{segment.correctLevel}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* 操作按钮 */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          共 <span className="text-yellow-400 font-mono">{segments.length}</span> 个片段
          {validated && validationResult && (
            <span className="ml-4">
              正确 <span className="text-green-400">{validationResult.correct.length}</span> / 
              错误 <span className="text-red-400">{validationResult.wrong.length}</span>
            </span>
          )}
        </div>

        <div className="flex gap-3">
          {validated ? (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleReset}
              className="cyber-button"
            >
              重新练习
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleValidate}
              className="cyber-button-primary"
            >
              验证答案
            </motion.button>
          )}
        </div>
      </div>

      {/* 验证结果 */}
      {validated && validationResult && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 space-y-4"
        >
          {validationResult.wrong.length === 0 ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <span className="text-green-300 font-display">完美！你正确识别了所有小句的层级。</span>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <XCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div>
                <span className="text-yellow-300">部分层级判断错误，请查看上方标注的正确层级</span>
                <p className="text-sm text-gray-400 mt-2">
                  提示：从句/定语从句等修饰成分的层级比它修饰的成分高一级
                </p>
              </div>
            </div>
          )}

          {/* 层级说明 */}
          <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-300 font-display">正确层级结构</span>
            </div>
            <div className="space-y-1 text-sm font-mono">
              {segments.map(seg => (
                <div 
                  key={seg.id} 
                  className="flex items-center gap-2"
                  style={{ paddingLeft: `${seg.correctLevel * 24}px` }}
                >
                  <span className={`
                    px-2 py-0.5 rounded text-xs
                    ${getLevelColor(seg.correctLevel) === 'cyan' ? 'bg-cyan-500/20 text-cyan-300' : ''}
                    ${getLevelColor(seg.correctLevel) === 'purple' ? 'bg-purple-500/20 text-purple-300' : ''}
                    ${getLevelColor(seg.correctLevel) === 'yellow' ? 'bg-yellow-500/20 text-yellow-300' : ''}
                    ${getLevelColor(seg.correctLevel) === 'pink' ? 'bg-pink-500/20 text-pink-300' : ''}
                    ${getLevelColor(seg.correctLevel) === 'orange' ? 'bg-orange-500/20 text-orange-300' : ''}
                    ${getLevelColor(seg.correctLevel) === 'green' ? 'bg-green-500/20 text-green-300' : ''}
                  `}>
                    L{seg.correctLevel}
                  </span>
                  <span className="text-gray-300">{seg.text}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
